import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import { SNETButton, AlertBox } from "shared/dist/components";
import Actions from "./Actions";
import UploadDemoFiles from "./UploadDemoFiles";
import { demoAlerts } from "./DemoFilesStatus";
import { aiServiceDetailsActions } from "../../../Services/Redux/actionCreators";
import classes from "./Demo.module.css";
import FormContentCard from "../../../Components/PageFormComponents/FormContentCard";

const Demo = () => {
    const dispatch = useDispatch();
    const serviceDetails = useSelector((state) => state.aiServiceDetails);
    const demoFiles = serviceDetails.assets.demoFiles;
    const demoFilesStatus = demoFiles.status;
    const [notification, setNotification] = useState({});
    const [selectedFile, setSelectedFile] = useState({ name: "", size: "", type: "" });

    useEffect(() => {
        setNotification(demoAlerts[demoFilesStatus]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demoFiles]);

    const handleCheckboxChange = () => {
        dispatch(
            aiServiceDetailsActions.setDemoComponentAvailable(
                !serviceDetails.demoComponentAvailable
            )
        );
    };

    const resetNotification = () => {
        setNotification({});
    };

    const DemoCreationComponent = () => {
        return (
            <Fragment>
                <span>The steps for creating a demo UI are:</span>
                <div className={classes.stepOneContainer}>
                    <Typography variant="subtitle1" className={classes.stepsHeading}>
                        Step 1: Download DApp source code
                    </Typography>
                    <Typography variant="subtitle2">
                        The DApp source code enables you to build and test the interface for your
                        service. You can also peruse available UI components at &nbsp;
                        <a
                            href="http://custom-ui.singularitynet.io.s3-website-us-east-1.amazonaws.com/?path=/story/alerts-alertbox--live-source"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            SingularityNet storybook
                        </a>
                        . This lists all common components used in the marketplace and can be reused
                        for your service. Download the package from the following link and follow
                        the instructions to set up a local environment.
                    </Typography>
                    <div className={classes.downloadBtn}>
                        <a href="https://github.com/singnet/snet-dapp/archive/master.zip" download>
                            <SNETButton
                                children="download source code"
                                color="primary"
                                variant="contained"
                            />
                        </a>
                    </div>
                    <hr />
                </div>
                <div className={classes.stepTwoContainer}>
                    <Typography variant="subtitle1" className={classes.stepsHeading}>
                        Step 2: Set Up Local Test Environment (ensure your node version is 10.20.0
                        or later)
                    </Typography>
                    <Typography variant="subtitle2">
                        Once you download the package extract the source code and follow the
                        instructions detailed &nbsp;
                        <a
                            href="https://dev.singularitynet.io/docs/products/DecentralizedAIPlatform/Sandbox/version-2/"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            here{" "}
                        </a>
                    </Typography>
                    <div className={classes.stepTwoBtnsContaier}>
                        <Typography variant="subtitle2">
                            Getting stuck or have questions?
                        </Typography>
                        <SNETButton
                            children="setup docs"
                            color="primary"
                            variant="outlined"
                            href="https://dev.singularitynet.io/docs/products/DecentralizedAIPlatform/Sandbox/version-2/"
                            target="_blank"
                            rel="noopener nofollow"
                        />
                        <SNETButton
                            children="contact us"
                            href="mailto:support@singularitynet.io"
                            color="primary"
                            variant="outlined"
                            target="_blank"
                        />
                    </div>
                    <hr />
                </div>
                <UploadDemoFiles
                    demoFiles={demoFiles}
                    error={!demoFiles.url}
                    selectedFile={selectedFile}
                    uploadDemoFilesDetails={setSelectedFile}
                    resetNotification={resetNotification}
                />
            </Fragment>
        );
    };

    return (
        <Fragment>
            <FormContentCard
                title="Demo Setup"
                titleComponent={
                    <FormControlLabel
                        className={classes.switch}
                        labelPlacement="end"
                        label="Enable Demo"
                        control={
                            <Switch
                                checked={Boolean(serviceDetails.demoComponentAvailable)}
                                onChange={handleCheckboxChange}
                                color="primary"
                            />
                        }
                    />
                }
                description={`
                    AI publishers can create a unique demo experience that users on the AI
                    Marketplace can engage with. A positive demo experience will encourage users
                    to engage and integrate your AI into their applications. We encourage you to
                    follow our best practices on how to properly set up your demo on our AI
                    Marketplace.
                    `}
            >
                {!!serviceDetails.demoComponentAvailable && <DemoCreationComponent />}
            </FormContentCard>

            {notification && Boolean(serviceDetails.demoComponentAvailable) && (
                <div className={classes.uploadStatusNotification}>
                    <AlertBox type={notification.type} message={notification.message} />
                </div>
            )}

            <Actions selectedFile={selectedFile} serviceDetails={serviceDetails} />
        </Fragment>
    );
};
export default Demo;
